import { useEffect, useMemo, useState } from 'react'
import { injectIntl, intlShape } from 'react-intl'
import Select from 'react-select'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'

import useDimensionSearch from 'utils/hooks/useDimensionSearch'
import useDimensionValueFetchCheckStore from 'utils/hooks/useDimensionValueFetchCheckStore'

import ClearIndicator from 'components/SelectReportDimension/components/ClearIndicator'
import MultiValueLabel from 'components/SelectReportDimension/components/MultiValueLabel'
import Option from 'components/SelectReportDimension/components/Option'
import GroupHeading from './components/GroupHeading'
import VirtualMenuList from './components/VirtualMenuList'
import useFlattenedDimensionData from './useFlattenedDimensionData'
import {
  getCustomFilter,
  getCustomStyles,
  getLoadedOptionValues,
  getSelectedOptions,
  hasTruncatedDimension,
  sortOptionsByRelevance,
} from './functions'
import messages from './messages'

const staticComponents = {
  ClearIndicator,
  MultiValueLabel,
  GroupHeading,
  Option,
}
const staticVirtualComponents = {
  ...staticComponents,
  MenuList: VirtualMenuList,
}

const propTypes = {
  companyCode: PropTypes.string,
  dimensionData: ImmutablePropTypes.list.isRequired,
  inputId: PropTypes.string,
  intl: intlShape.isRequired,
  isLoading: PropTypes.bool,
  onChange: PropTypes.func,
  onMultiValueClick: PropTypes.func,
  redirect: PropTypes.bool,
  searchLimit: PropTypes.number,
  selectedDimensionValueIds: PropTypes.array,
  selectionType: PropTypes.oneOf([
    'singleValuePerDimension',
    'multipleValuesPerDimension',
  ]),
  showOnlySelectedValues: PropTypes.bool,
}

const SelectBudgetDimensionIntl = (props) => {
  const {
    companyCode,
    dimensionData,
    inputId,
    intl,
    isDisabled,
    isLoading,
    onChange,
    redirect,
    selectedDimensionValueIds,
    showOnlySelectedValues,
    selectionType = 'singleValuePerDimension',
    includeOperative = false,
    searchLimit,
    ...rest
  } = props

  const defaultOptions = useFlattenedDimensionData(dimensionData)

  const [selectedValues, setSelectedValues] = useState(
    getSelectedOptions(selectedDimensionValueIds, defaultOptions)
  )

  const filteredOptions = useMemo(() => {
    if (selectionType === 'singleValuePerDimension') {
      return selectedValues?.length > 0
        ? defaultOptions.filter(
            (option) =>
              !selectedValues.some((value) => value.path[0] === option.path[0])
          )
        : defaultOptions
    }

    return defaultOptions
  }, [defaultOptions, selectedValues, selectionType])

  // Only search the backend when the loaded page might be truncated (unknown
  // limit → assume it is).
  const couldHaveMoreOnServer = useMemo(
    () =>
      searchLimit ? hasTruncatedDimension(dimensionData, searchLimit) : true,
    [dimensionData, searchLimit]
  )

  const [inputValue, setInputValue] = useState('')

  const [setSearchTerm, searchLoading] = useDimensionSearch({
    companyCode,
    includeOperative,
    debounce: 1000,
  })
  const loadedOptionValues = useMemo(
    () => getLoadedOptionValues(defaultOptions),
    [defaultOptions]
  )

  // Fetch only selected ids not already loaded (e.g. a restored selection beyond
  // the capped page); excluding loaded ones stops a `dimensionValues/list` fetch
  // on every pick. Memoized so the fetch effect skips unrelated renders.
  const valuesNeedingFetch = useMemo(
    () =>
      showOnlySelectedValues
        ? []
        : (selectedDimensionValueIds ?? []).filter(
            (id) =>
              !id?.toString().includes('blank') &&
              !loadedOptionValues.has(String(id))
          ),
    [showOnlySelectedValues, selectedDimensionValueIds, loadedOptionValues]
  )

  const [valuesLoading] = useDimensionValueFetchCheckStore(valuesNeedingFetch)

  // Choose default dimensionValue if only one is available and redirect is enabled
  useEffect(() => {
    if (
      filteredOptions.length === 1 &&
      filteredOptions[0].options.length === 1 &&
      selectedValues.length === 0 &&
      redirect
    ) {
      setSelectedValues([filteredOptions[0].options[0]])
      onChange([filteredOptions[0].options[0].value])
    }
  }, [filteredOptions, onChange, redirect, selectedValues])

  useEffect(() => {
    setSelectedValues(
      getSelectedOptions(selectedDimensionValueIds, defaultOptions)
    )
  }, [defaultOptions, selectedDimensionValueIds])

  const handleChange = (selected, props) => {
    switch (props.action) {
      case 'clear':
      case 'remove-value':
      case 'pop-value':
      case 'select-option':
        setSelectedValues(selected)
        onChange(selected.map((item) => item.value))
        break
      default:
        break
    }
  }

  const handleInputChange = (searchTerm, actionMeta) => {
    // Ignore react-select's automatic input clearing on selection/blur/close;
    // handleMenuClose does cleanup so the term persists across multi-selects.
    if (
      actionMeta?.action === 'set-value' ||
      actionMeta?.action === 'input-blur' ||
      actionMeta?.action === 'menu-close'
    ) {
      return
    }
    setInputValue(searchTerm)

    // Search the backend for the long tail beyond the loaded page (4+ chars,
    // only if not already complete). Trim first so "abc " stays a 3-char query.
    const trimmed = searchTerm.trim()
    setSearchTerm(couldHaveMoreOnServer && trimmed.length > 3 ? trimmed : '')
  }

  const handleMenuClose = () => {
    setInputValue('')
    setSearchTerm('')
  }

  // Surface the closest name match first while searching; keeps the default
  // (alphabetical) order when the box is empty.
  const orderedOptions = useMemo(
    () => sortOptionsByRelevance(filteredOptions, inputValue.trim()),
    [filteredOptions, inputValue]
  )

  const shouldVirtualize = useMemo(
    () => defaultOptions?.reduce((sum, g) => sum + g.options.length, 0) > 600,
    [defaultOptions]
  )

  return (
    <Select
      className="selectDimension"
      components={shouldVirtualize ? staticVirtualComponents : staticComponents}
      filterOption={getCustomFilter}
      isDisabled={isDisabled || showOnlySelectedValues}
      inputId={inputId}
      inputValue={inputValue}
      isLoading={isLoading || searchLoading || valuesLoading}
      isMulti
      onChange={handleChange}
      onInputChange={handleInputChange}
      onMenuClose={handleMenuClose}
      options={orderedOptions}
      placeholder={intl.formatMessage(messages.placeholder)}
      showOnlySelectedValues={showOnlySelectedValues}
      styles={getCustomStyles}
      value={selectedValues}
      aria-label={intl.formatMessage(messages.ariaLabel)}
      {...rest}
    />
  )
}

SelectBudgetDimensionIntl.propTypes = propTypes

export const SelectBudgetDimension = injectIntl(SelectBudgetDimensionIntl)

export default SelectBudgetDimension
